/* KBF: native Shopify data, Dawn cart drawer, and the demo's carousel/navigation motion. */
(() => {
  'use strict';
  document.documentElement.classList.add('kbf-js');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const root = () => window.Shopify?.routes?.root || '/';
  const cartURL = () => window.routes?.cart_url || `${root()}cart`;
  const jsonURL = (url) => `${url.replace(/\.(js|json)$/, '')}.json`;
  const selectors = '[data-kbf-carousel], [data-kbf-header], [data-kbf-search], [data-kbf-routine], [data-kbf-journal], [data-kbf-article]';
  const listen = (node, type, handler, controller, options = {}) => node.addEventListener(type, handler, { ...options, signal: controller.signal });

  class HeroCarousel {
    constructor(element) {
      this.element = element;
      this.controller = new AbortController();
      this.slides = [...element.querySelectorAll('[data-carousel-slide]')];
      this.dots = [...element.querySelectorAll('[data-carousel-dot]')];
      this.track = element.querySelector('[data-carousel-track]');
      this.play = element.querySelector('[data-carousel-play]');
      this.current = 0;
      this.userPaused = element.dataset.autoplay !== 'true' || reducedMotion.matches;
      this.speed = Math.max(3000, Number(element.dataset.speed) || 5000);
      this.hovered = false;
      this.focused = false;
      this.update(false);
      this.dots.forEach((dot, index) => listen(dot, 'click', () => this.goTo(index), this.controller));
      if (this.play) listen(this.play, 'click', () => {
        this.userPaused = !this.userPaused;
        this.updatePlay();
        this.schedule();
      }, this.controller);
      listen(element, 'mouseenter', () => { this.hovered = true; this.schedule(); }, this.controller);
      listen(element, 'mouseleave', () => { this.hovered = false; this.schedule(); }, this.controller);
      listen(element, 'focusin', () => { this.focused = true; this.schedule(); }, this.controller);
      listen(element, 'focusout', (event) => {
        this.focused = element.contains(event.relatedTarget);
        this.schedule();
      }, this.controller);
      listen(document, 'visibilitychange', () => this.schedule(), this.controller);
      listen(reducedMotion, 'change', () => {
        if (reducedMotion.matches) this.userPaused = true;
        this.update(false);
        this.schedule();
      }, this.controller);
      const carousel = element.querySelector('.carousel-container');
      listen(carousel, 'pointerdown', (event) => {
        if (event.target.closest('button')) return;
        this.touch = { x: event.clientX, y: event.clientY };
      }, this.controller);
      listen(carousel, 'pointerup', (event) => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch.x;
        const dy = event.clientY - this.touch.y;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.goTo(this.current + (dx > 0 ? -1 : 1));
        this.touch = null;
      }, this.controller);
      listen(carousel, 'pointercancel', () => { this.touch = null; }, this.controller);
      listen(carousel, 'keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.goTo(this.current + (event.key === 'ArrowRight' ? 1 : -1));
        this.dots[this.current]?.focus();
      }, this.controller);
      this.schedule();
    }
    goTo(index, user = true) {
      if (!this.slides.length) return;
      this.current = (index + this.slides.length) % this.slides.length;
      this.update(user);
      if (user) this.element.querySelector('[role="status"]').textContent = `Slide ${this.current + 1} of ${this.slides.length}`;
      this.schedule();
    }
    update(animate) {
      this.track.style.transition = reducedMotion.matches || !animate ? 'none' : 'transform 700ms cubic-bezier(.22,.61,.36,1)';
      this.track.style.transform = reducedMotion.matches ? 'none' : `translateX(${-this.current * 100}%)`;
      this.slides.forEach((slide, index) => {
        const active = index === this.current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (active) slide.querySelector('img')?.setAttribute('loading', 'eager');
        this.dots[index]?.classList.toggle('active', active);
        this.dots[index]?.setAttribute('aria-current', String(active));
      });
      this.updatePlay();
    }
    updatePlay() {
      if (!this.play) return;
      this.play.textContent = this.userPaused ? '▶' : '⏸';
      this.play.setAttribute('aria-label', this.userPaused ? 'Play carousel' : 'Pause carousel');
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.slides.length < 2 || this.userPaused || this.hovered || this.focused || document.hidden) return;
      this.timer = setTimeout(() => {
        this.current = (this.current + 1) % this.slides.length;
        this.update(true);
        this.schedule();
      }, this.speed);
    }
    destroy() { clearTimeout(this.timer); this.controller.abort(); }
  }

  function header(element) {
    const controller = new AbortController();
    const items = [...element.querySelectorAll('[data-nav-item]')];
    function setOpen(item, open) {
      item.classList.toggle('open', open);
      item.querySelector('button.top').setAttribute('aria-expanded', String(open));
      const drop = item.querySelector('.drop');
      drop.hidden = !open;
      drop.style.top = window.innerWidth <= 920 ? `${element.getBoundingClientRect().bottom}px` : '';
    }
    const closeAll = () => items.forEach((item) => setOpen(item, false));
    items.forEach((item) => {
      setOpen(item, false);
      const button = item.querySelector('button.top');
      listen(button, 'click', () => {
        // Hover already opened it on pointer devices: a click must not close it (hover-then-click UX).
        if (item.classList.contains('open') && item.matches(':hover') && window.matchMedia('(hover: hover)').matches) return;
        const open = !item.classList.contains('open');
        closeAll(); setOpen(item, open);
      }, controller);
      listen(item, 'mouseenter', () => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        closeAll(); setOpen(item, true);
      }, controller);
      listen(item, 'mouseleave', () => { if (!item.contains(document.activeElement)) setOpen(item, false); }, controller);
      listen(item, 'focusout', (event) => { if (!item.contains(event.relatedTarget)) setOpen(item, false); }, controller);
      listen(button, 'keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault(); closeAll(); setOpen(item, true);
        item.querySelector('.drop a')?.focus();
      }, controller);
    });
    listen(document, 'click', (event) => { if (!element.contains(event.target)) closeAll(); }, controller);
    listen(document, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = items.find((item) => item.classList.contains('open'));
      if (open) { setOpen(open, false); open.querySelector('button.top').focus(); }
    }, controller);
    listen(window, 'resize', closeAll, controller);
    listen(element, 'keydown', (event) => {
      if (event.target.matches('[data-joy-trigger]') && ['Enter', ' '].includes(event.key)) {
        event.preventDefault(); event.target.click();
      }
    }, controller);
    return { destroy: () => controller.abort() };
  }

  function predictiveSearch(form) {
    const controller = new AbortController();
    const input = form.querySelector('input[type="search"]');
    if (!input) return { destroy: () => controller.abort() };
    const ariaControlsId = input.getAttribute('aria-controls');
    const results = ariaControlsId ? document.getElementById(ariaControlsId) : null;
    if (!results) return { destroy: () => controller.abort() };
    let debounce;
    let request;
    let active = -1;
    const close = () => {
      clearTimeout(debounce); request?.abort();
      results.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1;
    };
    function row(product, index) {
      const link = document.createElement('a');
      link.className = 'hsrow'; link.href = product.url; link.id = `${input.id}-option-${index}`;
      link.setAttribute('role', 'option'); link.setAttribute('aria-selected', 'false');
      const thumb = document.createElement('span'); thumb.className = 'hsthumb';
      const imageURL = product.featured_image?.url || product.image;
      if (imageURL) {
        const image = document.createElement('img'); image.src = imageURL; image.alt = ''; image.width = 40; image.height = 40; thumb.append(image);
      }
      const info = document.createElement('span'); info.className = 'hsinfo';
      const title = document.createElement('b'); title.textContent = product.title;
      const vendor = document.createElement('small'); vendor.textContent = product.vendor || '';
      info.append(title, vendor); link.append(thumb, info);
      return link;
    }
    async function search() {
      const query = input.value.trim();
      if (query.length < 2) { close(); return; }
      request?.abort(); request = new AbortController();
      const url = new URL(jsonURL(window.routes?.predictive_search_url || `${root()}search/suggest`), location.origin);
      url.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last' });
      try {
        const response = await fetch(url, { signal: request.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (input.value.trim() !== query) return;
        const products = data.resources?.results?.products || [];
        results.replaceChildren(...products.map(row));
        const all = document.createElement('a'); all.className = 'hsall';
        all.href = `${form.action}?${new URLSearchParams({ q: query, type: 'product' })}`;
        all.textContent = products.length ? `See all results for "${query}" →` : `Search the shop for "${query}" →`;
        all.id = `${input.id}-option-all`; all.setAttribute('role', 'option'); all.setAttribute('aria-selected', 'false');
        results.append(all); results.hidden = false; active = -1;
        input.setAttribute('aria-expanded', 'true'); input.removeAttribute('aria-activedescendant');
      } catch (error) {
        if (error.name !== 'AbortError') close(); // The GET search form remains usable.
      }
    }
    listen(input, 'input', () => {
      close();
      if (input.value.trim().length >= 2) debounce = setTimeout(search, 180);
    }, controller);
    listen(input, 'keydown', (event) => {
      if (event.key === 'Escape') { if (!results.hidden) event.stopPropagation(); close(); return; }
      if (results.hidden) return;
      const options = [...results.querySelectorAll('[role="option"]')];
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        active = (active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === active)));
        input.setAttribute('aria-activedescendant', options[active].id);
        options[active].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && active >= 0) { event.preventDefault(); options[active].click(); }
    }, controller);
    listen(document, 'click', (event) => { if (!form.contains(event.target) && !results.contains(event.target)) close(); }, controller);
    return { destroy: () => { close(); controller.abort(); } };
  }

  function routine(element) {
    const controller = new AbortController();
    const videos = [...element.querySelectorAll('video.rt-doodle')];
    const button = element.querySelector('[data-routine-motion]');
    const visible = new Set();
    let paused = reducedMotion.matches;
    const sync = () => {
      videos.forEach((video) => {
        if (!paused && !document.hidden && visible.has(video)) video.play().catch(() => {});
        else video.pause();
      });
      if (button) button.textContent = paused ? 'Play illustrations' : 'Pause illustrations';
    };
    videos.forEach((video) => { video.controls = false; });
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => isIntersecting ? visible.add(target) : visible.delete(target));
      sync();
    }, { rootMargin: '100px' }) : null;
    videos.forEach((video) => observer ? observer.observe(video) : visible.add(video));
    if (button && videos.length) {
      button.hidden = false;
      listen(button, 'click', () => { paused = !paused; sync(); }, controller);
    }
    listen(reducedMotion, 'change', () => { paused = reducedMotion.matches; sync(); }, controller);
    listen(document, 'visibilitychange', sync, controller);
    sync();
    return { destroy: () => { controller.abort(); observer?.disconnect(); videos.forEach((video) => video.pause()); } };
  }

  function journal(element) {
    const controller = new AbortController();
    const toggle = element.querySelector('[data-journal-toggle]');
    const filters = element.querySelector('.journal-filters');
    if (toggle && filters) {
      const compact = window.matchMedia('(max-width: 600px)');
      const setExpanded = (expanded) => {
        filters.hidden = !expanded;
        toggle.setAttribute('aria-expanded', String(expanded));
      };
      const layout = () => { toggle.hidden = !compact.matches; setExpanded(!compact.matches); };
      listen(toggle, 'click', () => setExpanded(filters.hidden), controller);
      listen(compact, 'change', layout, controller);
      layout();
    }
    const strip = element.querySelector('.journal-fact-strip');
    const next = element.querySelector('[data-journal-next-fact]');
    if (strip && next && strip.children.length > 1) {
      next.hidden = false;
      listen(next, 'click', () => {
        const gap = parseFloat(getComputedStyle(strip).columnGap) || 0;
        const distance = strip.firstElementChild.getBoundingClientRect().width + gap;
        const atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
        strip.scrollTo({ left: atEnd ? 0 : strip.scrollLeft + distance, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      }, controller);
    }
    return { destroy: () => controller.abort() };
  }

  function articleContent(element) {
    // The import includes a plain Sources section; native details keeps all
    // links readable without JavaScript and supplies keyboard interaction.
    element.querySelectorAll('.jl-sources:not(details)').forEach((sources) => {
      const heading = sources.querySelector('h2');
      if (!heading) return;
      const details = document.createElement('details');
      details.className = `${sources.className} journal-sources`;
      if (sources.id) details.id = sources.id;
      const summary = document.createElement('summary');
      summary.textContent = heading.textContent;
      heading.remove();
      details.append(summary, ...sources.childNodes);
      sources.replaceWith(details);
    });
    return { destroy() {} };
  }

  function init(scope = document) {
    const nodes = [...scope.querySelectorAll(selectors)];
    if (scope.matches?.(selectors)) nodes.unshift(scope);
    nodes.forEach((element) => {
      if (instances.has(element)) return;
      let instance;
      if (element.matches('[data-kbf-carousel]')) instance = new HeroCarousel(element);
      else if (element.matches('[data-kbf-header]')) instance = header(element);
      else if (element.matches('[data-kbf-routine]')) instance = routine(element);
      else if (element.matches('[data-kbf-journal]')) instance = journal(element);
      else if (element.matches('[data-kbf-article]')) instance = articleContent(element);
      else instance = predictiveSearch(element);
      instances.set(element, instance);
    });
  }
  let searchTrigger;
  function closeSearch() {
    const modal = document.getElementById('searchWrap');
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove('kbf-search-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(searchTrigger);
    else searchTrigger?.focus();
  }
  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-search-open]');
    if (open) {
      const modal = document.getElementById('searchWrap');
      if (!modal) return;
      event.preventDefault(); searchTrigger = open; modal.hidden = false;
      document.body.classList.add('kbf-search-open');
      if (typeof trapFocus === 'function') trapFocus(modal, modal.querySelector('input'));
      else modal.querySelector('input')?.focus();
    }
    if (event.target.closest('[data-search-close]') || event.target.id === 'searchWrap') closeSearch();
    const cartLink = event.target.closest('#cart-icon-bubble');
    const drawer = document.querySelector('cart-drawer');
    // Dawn's original listener is installed only once; delegation also covers editor reloads.
    if (cartLink && drawer && !event.defaultPrevented) { event.preventDefault(); drawer.open(cartLink); }
  });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeSearch(); });
  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-variant-select]')) return;
    const option = event.target.selectedOptions[0];
    const card = event.target.closest('.pcard');
    const price = card.querySelector('[data-product-price]');
    price.textContent = option.dataset.price;
    if (option.dataset.compare) {
      const compare = document.createElement('s'); compare.textContent = option.dataset.compare;
      price.append(document.createTextNode(' '), compare);
    }
    const badge = card.querySelector('[data-card-sale]');
    if (badge) badge.hidden = !option.dataset.compare;
    event.target.form.elements.quantity.value = option.dataset.min || '1';
  });
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-kbf-add]');
    if (!form) return;
    event.preventDefault();
    if (form.dataset.busy) return;
    const button = form.querySelector('button[type="submit"]');
    const message = form.querySelector('[data-cart-message]');
    const original = button.textContent;
    const drawer = document.querySelector('cart-drawer');
    form.dataset.busy = 'true'; button.disabled = true; button.textContent = 'Adding…'; message.textContent = '';
    const body = new FormData(form);
    if (drawer) {
      body.set('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
      body.set('sections_url', window.location.pathname);
      drawer.setActiveElement(button);
    }
    let added = false;
    try {
      const response = await fetch(jsonURL(window.routes?.cart_add_url || form.action), { method: 'POST', body, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add this item. Please try again.');
      added = true;
      const sectionsReady = drawer && drawer.getSectionsToRender().every((section) => typeof data.sections?.[section.id] === 'string');
      if (sectionsReady) {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kbf-product-form', productVariantId: body.get('id'), cartData: data });
        message.textContent = 'Added to your bag.';
      } else window.location.assign(cartURL());
    } catch (error) {
      if (added) window.location.assign(cartURL());
      else message.textContent = error.message || 'Could not add this item. Please try again.';
    } finally {
      delete form.dataset.busy; button.disabled = false; button.textContent = original;
    }
  });

  let lenis;
  function smoothScroll() {
    lenis?.destroy(); lenis = null;
    if (reducedMotion.matches || !window.Lenis || window.Shopify?.designMode || document.querySelector('[data-kbf-header]')?.dataset.smoothScroll !== 'true') return;
    lenis = new window.Lenis({ lerp: 0.12, wheelMultiplier: 1, smoothWheel: true, syncTouch: false, autoRaf: true, prevent: (node) => node.closest?.('cart-drawer, .searchwrap, .drop, [data-lenis-prevent]') });
    syncScrollLock();
  }
  function syncScrollLock() {
    if (!lenis) return;
    if (document.body.classList.contains('overflow-hidden') || document.body.classList.contains('kbf-search-open')) lenis.stop();
    else lenis.start();
  }
  new MutationObserver(syncScrollLock).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  reducedMotion.addEventListener('change', smoothScroll);
  document.addEventListener('shopify:section:load', (event) => { init(event.target); smoothScroll(); });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('[data-kbf-header]')) closeSearch();
    instances.forEach((instance, element) => { if (event.target.contains(element)) { instance.destroy(); instances.delete(element); } });
  });
  document.addEventListener('shopify:block:select', (event) => {
    const carousel = event.target.closest('[data-kbf-carousel]');
    const instance = instances.get(carousel);
    if (!instance) return;
    const index = instance.slides.indexOf(event.target.closest('[data-carousel-slide]'));
    if (index >= 0) { instance.userPaused = true; instance.goTo(index); }
  });
  init(); smoothScroll();
})();
